import React from 'react';
import { Link } from 'react-router-dom';
import LocalHelmet from '../common/LocalHelmet';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { HOSTED_IMAGES } from '../utils/imageHosting';

const { FiArrowRight, FiTrendingDown, FiUsers, FiCpu, FiAlertTriangle, FiTarget, FiShield, FiExternalLink, FiClock, FiZap, FiTrendingUp } = FiIcons;

const CaseForChange = () => {
  const challenges = [
    {
      icon: FiTrendingDown,
      title: 'Volatile Markets',
      description: 'Economic uncertainty, supply chain disruptions, and changing consumer behavior make traditional planning obsolete.',
      impact: 'Companies using static annual plans see 23% lower performance than those with adaptive frameworks.',
      source: 'McKinsey Global Institute, 2023'
    },
    {
      icon: FiUsers,
      title: 'Shifting Expectations',
      description: 'Customers demand personalized experiences while employees expect inclusive, purpose-driven workplaces.',
      impact: 'Brands that ignore inclusion see 15% higher customer churn and 40% higher employee turnover.',
      source: 'Deloitte Global Human Capital Trends, 2024'
    },
    {
      icon: FiCpu,
      title: 'AI Disruption',
      description: 'Artificial intelligence is reshaping how we work, decide, and compete but adoption without strategy creates chaos.',
      impact: 'Companies with strategic AI adoption outperform peers by 2.3x in revenue growth.',
      source: 'MIT Sloan Management Review, 2024'
    },
    {
      icon: FiAlertTriangle,
      title: 'Decision Paralysis',
      description: 'Too much data, too many tools, too many opinions. Leaders struggle to cut through noise and act with confidence.',
      impact: 'Decision delays cost Fortune 500 companies an average of $250M annually in missed opportunities.',
      source: 'Harvard Business Review, 2023'
    }
  ];

  const oldVsNew = [
    {
      old: 'Annual strategic planning',
      new: 'Adaptive, data-driven frameworks',
      why: 'Markets change too fast for yearly plans'
    },
    {
      old: 'Gut-based decisions',
      new: 'Evidence-backed choices',
      why: 'Intuition alone isn\'t enough anymore'
    },
    {
      old: 'Siloed analytics teams',
      new: 'Integrated decision support',
      why: 'Strategy and data must work together'
    },
    {
      old: 'AI as a side project',
      new: 'AI as strategic capability',
      why: 'Late adopters get left behind'
    },
    {
      old: 'Diversity as compliance',
      new: 'Inclusion as competitive advantage',
      why: 'Diverse teams outperform by 35%'
    }
  ];

  const urgency = [
    {
      timeframe: 'Right Now',
      icon: FiClock,
      image: HOSTED_IMAGES.caseForChange.dataAnalysis,
      consequence: 'Your competitors are building adaptive capabilities while you\'re stuck in annual planning cycles.',
      action: 'Start with data frameworks that can evolve with your business.',
      urgencyLevel: 'immediate',
      color: 'yellow'
    },
    {
      timeframe: 'Next 6 Months',
      icon: FiZap,
      image: HOSTED_IMAGES.caseForChange.analytics,
      consequence: 'Market shifts will accelerate. Companies without real-time decision support will fall behind.',
      action: 'Implement AI-enhanced insights and scenario planning.',
      urgencyLevel: 'critical',
      color: 'orange'
    },
    {
      timeframe: 'Next 2 Years',
      icon: FiTrendingUp,
      image: HOSTED_IMAGES.caseForChange.growth,
      consequence: 'The gap between adaptive and static organizations will become insurmountable.',
      action: 'Build inclusive, data-driven culture as your sustainable advantage.',
      urgencyLevel: 'existential',
      color: 'red'
    }
  ];

  const inclusionStats = [
    {
      stat: 'Diverse teams make better decisions 87% of the time',
      source: 'Harvard Business Review, 2023'
    },
    {
      stat: 'Inclusive companies are 35% more likely to outperform peers',
      source: 'McKinsey & Company, 2024'
    },
    {
      stat: 'Neurodivergent teams solve problems 30% faster',
      source: 'Accenture Research, 2024'
    },
    {
      stat: 'LGBTQIA-friendly brands see 15% higher customer loyalty',
      source: 'Nielsen Global Survey, 2023'
    }
  ];

  return (
    <>
      <LocalHelmet prioritizeSeoTags>
      <title>Why Change? | Richardson Strategy Group</title>
      <meta
        name="description"
        content="See why clarity, adaptation, and action beat outdated strategy. Richardson Strategy Group helps leaders stay ahead by making real change possible."
      />
      <link rel="canonical" href="https://richardsonstrategy.group/case-for-change" />
      {/* Open Graph */}
      <meta property="og:site_name" content="Richardson Strategy Group" />
      <meta property="og:type" content="website" />
      <meta
        property="og:title"
        content="Why Change? | Richardson Strategy Group"
      />
      <meta
        property="og:description"
        content="Don't fall behind. See why decision-makers update their strategy for real impact and speed."
      />
      <meta property="og:url" content="https://richardsonstrategy.group/case-for-change" />
      <meta
        property="og:image"
        content="https://raw.githubusercontent.com/richardsonstrategygroup/rsg-assets/main/social/og-default.jpg"
      />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:title"
        content="Why Change? | Richardson Strategy Group"
      />
      <meta
        name="twitter:description"
        content="Real growth demands real change. Here's how leaders decide when to move."
      />
      <meta
        name="twitter:image"
        content="https://raw.githubusercontent.com/richardsonstrategygroup/rsg-assets/main/social/twitter-card.jpg"
      />
      {/* JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Why Change?',
          url: 'https://richardsonstrategy.group/case-for-change',
          description:
            'Strategic advice for adapting and driving change in a fast world.',
        })}
      </script>
    </LocalHelmet>


      <div className="min-h-screen py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-16 md:mb-20">
            <div>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-3xl sm:text-4xl md:text-5xl font-bold text-secondary-900 mb-4 md:mb-6 leading-tight"
              >
                Why Clarity & Inclusion Matter Now
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-lg md:text-xl text-secondary-600 leading-relaxed"
              >
                Are you still relying on last year’s playbook? Today, new customer expectations and quick market moves mean you need clarity right now. The cost of waiting isn’t just numbers, it’s opportunities lost that you’ll never see coming.
              </motion.p>
            </div>
            <div>
              <img
                src={HOSTED_IMAGES.caseForChange.analytics}
                alt="Business analytics and data visualization"
                className="rounded-2xl shadow-xl w-full h-64 md:h-96 object-cover"
              />
            </div>
          </div>

          {/* Current Challenges */}
          <div className="mb-16 md:mb-20">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-secondary-900 mb-8 md:mb-12 text-center">
              The Perfect Storm
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {challenges.map((challenge, index) => (
                <motion.div
                  key={challenge.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white p-4 md:p-6 rounded-xl shadow-sm border border-secondary-100 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={challenge.icon} className="w-5 h-5 md:w-6 md:h-6 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base md:text-lg font-semibold text-secondary-900 mb-2">{challenge.title}</h3>
                      <p className="text-secondary-600 mb-3 leading-relaxed text-sm md:text-base">{challenge.description}</p>
                      <div className="bg-red-50 p-3 rounded-lg mb-2">
                        <p className="text-red-800 text-xs md:text-sm font-medium">{challenge.impact}</p>
                      </div>
                      <p className="text-xs text-secondary-500 italic">Source: {challenge.source}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Old vs New - Enhanced Visual */}
          <div className="bg-gradient-to-br from-secondary-50 to-primary-50 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-12 md:py-20 mb-16 md:mb-20">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-secondary-900 mb-8 md:mb-12 text-center">
                The Shift That's Happening
              </h2>
              <div className="space-y-4 md:space-y-6">
                {oldVsNew.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="bg-white p-4 md:p-6 rounded-xl shadow-md border border-secondary-200 hover:shadow-lg transition-all duration-300"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center">
                      <div className="md:col-span-2 text-center md:text-left">
                        <div className="inline-block bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-semibold mb-2">
                          Old Way
                        </div>
                        <div className="text-secondary-700 font-medium">{item.old}</div>
                      </div>
                      
                      <div className="flex justify-center">
                        <div className="bg-primary-100 rounded-full p-3">
                          <SafeIcon icon={FiArrowRight} className="w-5 h-5 md:w-6 md:h-6 text-primary-600" />
                        </div>
                      </div>
                      
                      <div className="md:col-span-2 text-center md:text-right">
                        <div className="inline-block bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold mb-2">
                          New Way
                        </div>
                        <div className="text-secondary-700 font-medium mb-2">{item.new}</div>
                        <div className="text-secondary-500 text-sm italic">{item.why}</div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Urgency Timeline - Redesigned Visual */}
          <div className="mb-16 md:mb-20">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-secondary-900 mb-8 md:mb-12 text-center">
              The Cost of Waiting
            </h2>
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
                {urgency.map((item, index) => (
                  <motion.div
                    key={item.timeframe}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    className="group relative"
                  >
                    {/* Card Container */}
                    <div className={`relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 ${
                      item.color === 'yellow' ? 'bg-gradient-to-br from-yellow-50 to-yellow-100 border-2 border-yellow-200' :
                      item.color === 'orange' ? 'bg-gradient-to-br from-orange-50 to-orange-100 border-2 border-orange-200' :
                      'bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-200'
                    }`}>
                      
                      {/* Header Image */}
                      <div className="relative h-32 md:h-40 overflow-hidden">
                        <img
                          src={item.image}
                          alt={`${item.timeframe} urgency`}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className={`absolute inset-0 bg-gradient-to-t ${
                          item.color === 'yellow' ? 'from-yellow-900/60 to-transparent' :
                          item.color === 'orange' ? 'from-orange-900/60 to-transparent' :
                          'from-red-900/60 to-transparent'
                        }`}></div>
                        
                        {/* Floating Icon */}
                        <div className={`absolute top-4 right-4 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center ${
                          item.color === 'yellow' ? 'bg-yellow-500 text-yellow-900' :
                          item.color === 'orange' ? 'bg-orange-500 text-orange-900' :
                          'bg-red-500 text-red-900'
                        } shadow-lg`}>
                          <SafeIcon icon={item.icon} className="w-5 h-5 md:w-6 md:h-6" />
                        </div>
                        
                        {/* Timeframe Badge */}
                        <div className="absolute bottom-4 left-4">
                          <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                            item.color === 'yellow' ? 'bg-yellow-500 text-yellow-900' :
                            item.color === 'orange' ? 'bg-orange-500 text-orange-900' :
                            'bg-red-500 text-red-900'
                          } shadow-md`}>
                            {item.timeframe}
                          </div>
                        </div>
                      </div>
                      
                      {/* Content */}
                      <div className="p-4 md:p-6">
                        <h3 className={`font-bold text-base md:text-lg mb-3 ${
                          item.color === 'yellow' ? 'text-yellow-900' :
                          item.color === 'orange' ? 'text-orange-900' :
                          'text-red-900'
                        }`}>
                          What Happens If You Wait
                        </h3>
                        <p className="text-secondary-700 mb-4 text-sm md:text-base leading-relaxed">
                          {item.consequence}
                        </p>
                        
                        {/* Action Card */}
                        <div className={`p-3 md:p-4 rounded-lg border-l-4 ${
                          item.color === 'yellow' ? 'bg-white border-yellow-500' :
                          item.color === 'orange' ? 'bg-white border-orange-500' :
                          'bg-white border-red-500'
                        } shadow-sm`}>
                          <h4 className={`font-semibold mb-2 text-sm ${
                            item.color === 'yellow' ? 'text-yellow-900' :
                            item.color === 'orange' ? 'text-orange-900' :
                            'text-red-900'
                          }`}>
                            What You Can Do Now:
                          </h4>
                          <p className="text-secondary-700 text-xs md:text-sm leading-relaxed">
                            {item.action}
                          </p>
                        </div>
                      </div>
                      
                      {/* Urgency Indicator */}
                      <div className={`absolute top-0 left-0 w-full h-1 ${
                        item.color === 'yellow' ? 'bg-gradient-to-r from-yellow-400 to-yellow-600' :
                        item.color === 'orange' ? 'bg-gradient-to-r from-orange-400 to-orange-600' :
                        'bg-gradient-to-r from-red-400 to-red-600'
                      }`}></div>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              {/* Bottom CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="text-center mt-8 md:mt-12"
              >
                <div className="bg-secondary-900 text-white p-4 md:p-6 rounded-xl max-w-2xl mx-auto">
                  <h3 className="font-bold text-base md:text-lg mb-2">The Bottom Line</h3>
                  <p className="text-secondary-200 text-sm md:text-base">
                    Every month of delay costs you market position, talent retention, and competitive advantage. 
                    The question isn't whether to act, it's how quickly you can start.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>

          {/* The Inclusion Imperative */}
          <div className="bg-primary-50 p-6 md:p-12 rounded-2xl mb-16 md:mb-20">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-start space-x-4 mb-6">
                <SafeIcon icon={FiShield} className="w-6 h-6 md:w-8 md:h-8 text-primary-600 mt-1" />
                <h2 className="text-2xl md:text-3xl font-bold text-secondary-900">Why Inclusion Isn't Optional</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                <div>
                  <h3 className="font-semibold text-secondary-900 mb-3 text-base md:text-lg">The Business Case</h3>
                  <div className="space-y-3">
                    {inclusionStats.map((item, index) => (
                      <div key={index} className="bg-white p-3 rounded-lg border border-primary-200">
                        <p className="text-secondary-600 text-sm md:text-base mb-1">• {item.stat}</p>
                        <div className="flex items-center space-x-1 text-xs text-secondary-500">
                          <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
                          <span>Source: {item.source}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-secondary-900 mb-3 text-base md:text-lg">The Human Case</h3>
                  <div className="bg-white p-4 rounded-lg border border-primary-200">
                    <p className="text-secondary-600 leading-relaxed text-sm md:text-base">
                      Beyond the numbers, inclusion is about building organizations where every person 
                      can bring their whole self to work and contribute their unique perspective to solving 
                      complex problems. Growth without humanity isn't growth worth chasing.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-secondary-900 mb-4 md:mb-6">
              The Time for Change is Now
            </h2>
            <p className="text-base md:text-lg text-secondary-600 mb-6 md:mb-8 max-w-3xl mx-auto leading-relaxed">
              Every day you wait is a day your competitors get further ahead. But here's the good news: 
              you don't have to figure this out alone. We've helped dozens of organizations make this transition successfully.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-primary-600 text-white px-6 md:px-8 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors inline-flex items-center justify-center space-x-2"
              >
                <span>Let's Talk Strategy</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </Link>
              <Link
                to="/services"
                className="border border-secondary-300 text-secondary-700 px-6 md:px-8 py-3 rounded-lg font-medium hover:bg-secondary-50 transition-colors"
              >
                See How We Help
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CaseForChange;